import { Children, isValidElement, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ChangeEvent,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { createPortal } from 'react-dom'
import {
  formatDateDisplay,
  formatDateInputValue,
  parseDateInputValue,
} from './dateFormat'
import type { AppTone } from './AppUi'
import { cx } from './utils'

const optionToneDot: Record<AppTone, string> = {
  violet: 'bg-violet-500',
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  zinc: 'bg-zinc-400',
  sky: 'bg-sky-500',
  bronze: 'bg-orange-500',
  silver: 'bg-slate-400',
  gold: 'bg-yellow-500',
}

type ControlSize = 'sm' | 'md' | 'lg'

const sizeClass: Record<ControlSize, string> = {
  sm: 'rounded-xl px-3 py-2 text-sm',
  md: 'rounded-xl px-3 py-2.5 text-base',
  lg: 'rounded-2xl px-4 py-3.5 text-base',
}

const controlHeightClass: Record<ControlSize, string> = {
  sm: 'h-[38px]',
  md: 'h-[46px]',
  lg: 'h-[54px]',
}

const selectButtonSizeClass: Record<ControlSize, string> = {
  sm: 'rounded-xl py-2 pl-3 text-sm',
  md: 'rounded-xl py-2.5 pl-3 text-base',
  lg: 'rounded-2xl py-3.5 pl-4 text-base',
}

function ClearIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path strokeLinecap="round" d="M6 6l8 8M14 6l-8 8" />
    </svg>
  )
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden
    >
      <path strokeLinecap="round" d="M6 3v3M14 3v3" />
      <rect x="3" y="5" width="14" height="12" rx="2" />
      <path strokeLinecap="round" d="M3 9h14" />
    </svg>
  )
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" />
    </svg>
  )
}

export const formLabelClass =
  'mb-2 block text-sm font-medium text-zinc-800 dark:text-zinc-200'

export function controlClass({
  size = 'lg',
  className,
}: {
  size?: ControlSize
  className?: string
} = {}) {
  return cx(
    'w-full border border-zinc-200 bg-white text-zinc-900 shadow-sm outline-none transition placeholder:text-zinc-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/25 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-violet-400 dark:focus:ring-violet-400/20',
    sizeClass[size],
    className,
  )
}

const formLabelRequiredClass =
  'text-red-700 dark:text-red-400'

export function Field({
  label,
  htmlFor,
  hint,
  required,
  children,
}: {
  label: ReactNode
  htmlFor: string
  hint?: ReactNode
  required?: boolean
  children: ReactNode
}) {
  return (
    <div className="text-left">
      <label
        htmlFor={htmlFor}
        className={cx(formLabelClass, required && formLabelRequiredClass)}
      >
        {label}
        {required ? (
          <span className="text-red-600 dark:text-red-400" aria-hidden>
            {' '}
            *
          </span>
        ) : null}
      </label>
      {children}
      {hint ? (
        <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">{hint}</p>
      ) : null}
    </div>
  )
}

export function FieldLabel({
  className,
  ...props
}: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cx(formLabelClass, className)} {...props} />
}

function CheckboxClearIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden
    >
      <path strokeLinecap="round" d="M4 6h12M4 10h12M4 14h8" />
    </svg>
  )
}

export function CheckboxDropdownOption({
  checked,
  children,
  onToggle,
  tone,
  variant = 'default',
}: {
  checked: boolean
  children: ReactNode
  onToggle: () => void
  tone?: AppTone
  variant?: 'default' | 'clear'
}) {
  if (variant === 'clear') {
    return (
      <button
        type="button"
        onClick={onToggle}
        className={cx(
          'flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm font-medium transition',
          checked
            ? 'bg-violet-100/90 text-violet-900 ring-1 ring-violet-200/80 dark:bg-violet-950/55 dark:text-violet-100 dark:ring-violet-800/60'
            : 'text-zinc-600 hover:bg-zinc-100/80 dark:text-zinc-300 dark:hover:bg-zinc-800/80',
        )}
      >
        <span
          className={cx(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition',
            checked
              ? 'bg-violet-600 text-white dark:bg-violet-500'
              : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400',
          )}
          aria-hidden
        >
          <CheckboxClearIcon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1 truncate">{children}</span>
      </button>
    )
  }

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      className={cx(
        'flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm transition',
        checked
          ? 'bg-violet-50/90 text-violet-900 ring-1 ring-violet-100 dark:bg-violet-950/40 dark:text-violet-50 dark:ring-violet-900/50'
          : 'text-zinc-700 hover:bg-zinc-50/90 dark:text-zinc-200 dark:hover:bg-zinc-800/70',
      )}
    >
      <span
        className={cx(
          'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] text-[10px] font-bold transition',
          checked
            ? 'border-violet-600 bg-violet-600 text-white shadow-sm dark:border-violet-400 dark:bg-violet-500'
            : 'border-zinc-300/90 bg-white text-transparent dark:border-zinc-500 dark:bg-zinc-900',
        )}
        aria-hidden
      >
        ✓
      </span>
      <span className="min-w-0 flex-1 truncate font-medium">{children}</span>
      {tone ? (
        <span
          className={cx(
            'h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white dark:ring-zinc-900',
            optionToneDot[tone],
            checked ? 'opacity-100' : 'opacity-50',
          )}
          aria-hidden
        />
      ) : null}
    </button>
  )
}

export type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  controlSize?: ControlSize
}

export function TextInput({
  controlSize = 'lg',
  className,
  ...props
}: TextInputProps) {
  return (
    <input
      className={controlClass({ size: controlSize, className })}
      {...props}
    />
  )
}

function normalizeDateValue(value: string): string {
  const date = parseDateInputValue(value)
  return date ? formatDateInputValue(date) : ''
}

const weekdayLabels = ['Δε', 'Τρ', 'Τε', 'Πε', 'Πα', 'Σα', 'Κυ']

const monthFormatter = new Intl.DateTimeFormat('el-GR', {
  month: 'long',
  year: 'numeric',
})

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1)
}

function isSameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function buildCalendarDays(viewMonth: Date): Array<Date | null> {
  const firstDay = startOfMonth(viewMonth)
  const month = firstDay.getMonth()
  const leadingEmptyDays = (firstDay.getDay() + 6) % 7
  const days: Array<Date | null> = Array.from({ length: leadingEmptyDays }, () => null)
  const cursor = new Date(firstDay)

  while (cursor.getMonth() === month) {
    days.push(new Date(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }

  while (days.length % 7 !== 0) {
    days.push(null)
  }

  return days
}

export type DateInputProps = {
  id: string
  value: string
  onChange: (value: string) => void
  controlSize?: ControlSize
  className?: string
  clearLabel?: string
  disabled?: boolean
}

export function DateInput({
  id,
  value,
  onChange,
  controlSize = 'sm',
  className,
  clearLabel = 'Καθαρισμός',
  disabled = false,
}: DateInputProps) {
  const dateValue = normalizeDateValue(value)
  const selectedDate = useMemo(() => parseDateInputValue(dateValue), [dateValue])
  const [isOpen, setIsOpen] = useState(false)
  const [viewMonth, setViewMonth] = useState(() =>
    startOfMonth(selectedDate ?? new Date()),
  )
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 })
  const calendarDays = useMemo(() => buildCalendarDays(viewMonth), [viewMonth])
  const today = new Date()

  function updatePopoverPosition() {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return

    const popoverWidth = 288
    const popoverHeight = 360
    const margin = 8
    const left = Math.min(
      Math.max(rect.left, margin),
      window.innerWidth - popoverWidth - margin,
    )
    const hasRoomBelow = rect.bottom + margin + popoverHeight <= window.innerHeight
    const top = hasRoomBelow
      ? rect.bottom + margin
      : Math.max(margin, rect.top - popoverHeight - margin)

    setPopoverPosition({ top, left })
  }

  useEffect(() => {
    const nextSelectedDate = parseDateInputValue(dateValue)
    if (nextSelectedDate) {
      setViewMonth(startOfMonth(nextSelectedDate))
    }
  }, [dateValue])

  useEffect(() => {
    if (!isOpen) return

    function closeOnOutsidePointerDown(e: PointerEvent) {
      const target = e.target as Node
      if (
        !rootRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', closeOnOutsidePointerDown)
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointerDown)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    updatePopoverPosition()
    window.addEventListener('resize', updatePopoverPosition)
    window.addEventListener('scroll', updatePopoverPosition, true)
    return () => {
      window.removeEventListener('resize', updatePopoverPosition)
      window.removeEventListener('scroll', updatePopoverPosition, true)
    }
  }, [isOpen])

  function togglePicker() {
    if (disabled) return
    if (!isOpen) {
      setViewMonth(startOfMonth(selectedDate ?? new Date()))
      updatePopoverPosition()
    }
    setIsOpen((current) => !current)
  }

  function selectDate(date: Date) {
    onChange(formatDateInputValue(date))
    setIsOpen(false)
  }

  function clearDate() {
    onChange('')
    setIsOpen(false)
  }

  return (
    <div
      ref={rootRef}
      className={cx(
        'relative flex flex-col gap-2 sm:flex-row sm:items-center',
        className,
      )}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setIsOpen(false)
      }}
    >
      <button
        ref={buttonRef}
        id={id}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={`${id}-calendar`}
        onClick={togglePicker}
        disabled={disabled}
        className={cx(
          controlClass({ size: controlSize }),
          controlHeightClass[controlSize],
          'flex items-center justify-between gap-3 text-left disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-500 dark:disabled:bg-zinc-800/70 dark:disabled:text-zinc-500 sm:max-w-xs',
          !dateValue && 'text-zinc-400 dark:text-zinc-500',
        )}
      >
        <span>
          {selectedDate
            ? formatDateDisplay(selectedDate)
            : 'Επ. Ημ/νίας'}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {dateValue ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label={clearLabel}
              title={clearLabel}
              onClick={(e) => {
                e.stopPropagation()
                clearDate()
              }}
              className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-100 text-zinc-500 ring-1 ring-zinc-200 transition hover:bg-red-50 hover:text-red-600 hover:ring-red-200 dark:bg-zinc-800 dark:text-zinc-400 dark:ring-zinc-700 dark:hover:bg-red-950/50 dark:hover:text-red-300 dark:hover:ring-red-900/60"
            >
              <ClearIcon className="h-3.5 w-3.5" />
            </span>
          ) : null}
          <CalendarIcon className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
        </span>
      </button>
      {isOpen && typeof document !== 'undefined' ? createPortal(
        <div
          ref={popoverRef}
          id={`${id}-calendar`}
          role="dialog"
          aria-label="Επιλογή ημερομηνίας"
          style={{ top: popoverPosition.top, left: popoverPosition.left }}
          className="fixed z-[9999] w-72 rounded-2xl border border-zinc-200 bg-white p-3 shadow-xl dark:border-zinc-700 dark:bg-zinc-900"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setIsOpen(false)
          }}
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <button
              type="button"
              aria-label="Προηγούμενος μήνας"
              onClick={() => setViewMonth((current) => addMonths(current, -1))}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-white"
            >
              ‹
            </button>
            <p className="text-sm font-semibold capitalize text-zinc-900 dark:text-zinc-100">
              {monthFormatter.format(viewMonth)}
            </p>
            <button
              type="button"
              aria-label="Επόμενος μήνας"
              onClick={() => setViewMonth((current) => addMonths(current, 1))}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-white"
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {weekdayLabels.map((day) => (
              <div
                key={day}
                className="py-1 text-[11px] font-semibold uppercase text-zinc-400 dark:text-zinc-500"
              >
                {day}
              </div>
            ))}
            {calendarDays.map((day, index) =>
              day ? (
                <button
                  key={formatDateInputValue(day)}
                  type="button"
                  onClick={() => selectDate(day)}
                  className={cx(
                    'flex h-9 items-center justify-center rounded-lg text-sm font-medium transition',
                    isSameDay(day, selectedDate)
                      ? 'bg-violet-600 text-white shadow-sm hover:bg-violet-700'
                      : isSameDay(day, today)
                        ? 'bg-violet-50 text-violet-700 ring-1 ring-violet-200 hover:bg-violet-100 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-900/70 dark:hover:bg-violet-950/70'
                        : 'text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800',
                  )}
                >
                  {day.getDate()}
                </button>
              ) : (
                <span key={`empty-${index}`} aria-hidden />
              ),
            )}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-zinc-100 pt-3 dark:border-zinc-800">
            <button
              type="button"
              onClick={() => selectDate(new Date())}
              className="rounded-lg px-2.5 py-1.5 text-sm font-semibold text-violet-700 transition hover:bg-violet-50 dark:text-violet-300 dark:hover:bg-violet-950/50"
            >
              Σήμερα
            </button>
            {dateValue ? (
              <button
                type="button"
                onClick={clearDate}
                className="rounded-lg px-2.5 py-1.5 text-sm font-semibold text-zinc-500 transition hover:bg-zinc-100 hover:text-red-600 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-red-300"
              >
                {clearLabel}
              </button>
            ) : null}
          </div>
        </div>
      , document.body) : null}
    </div>
  )
}

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  controlSize?: ControlSize
  clearLabel?: string
  clearable?: boolean
  onClear?: () => void
}

type ParsedSelectOption = {
  value: string
  label: ReactNode
  disabled: boolean
}

function optionLabelToText(label: ReactNode): string {
  if (typeof label === 'string' || typeof label === 'number') return String(label)
  if (Array.isArray(label)) return label.map(optionLabelToText).join('')
  return ''
}

function parseSelectOptions(children: ReactNode): ParsedSelectOption[] {
  const options: ParsedSelectOption[] = []

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return
    const optionProps = child.props as {
      value?: string | number
      children?: ReactNode
      disabled?: boolean
    }
    const label = optionProps.children
    options.push({
      value: String(optionProps.value ?? optionLabelToText(label)),
      label,
      disabled: Boolean(optionProps.disabled),
    })
  })

  return options
}

function selectValueToString(value: SelectProps['value']): string {
  if (Array.isArray(value)) return String(value[0] ?? '')
  if (value === undefined || value === null) return ''
  return String(value)
}

export function Select({
  controlSize = 'lg',
  className,
  clearLabel = 'Καθαρισμός επιλογής',
  clearable = false,
  disabled,
  onClear,
  style,
  value,
  children,
  id,
  name,
  onBlur,
  onChange,
  onClick,
  onFocus,
  ...props
}: SelectProps) {
  const options = useMemo(() => parseSelectOptions(children), [children])
  const selectedValue = selectValueToString(value)
  const selectedOption = options.find((option) => option.value === selectedValue)
  const [isOpen, setIsOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [popoverLayout, setPopoverLayout] = useState({
    top: 0,
    left: 0,
    width: 180,
    maxHeight: 280,
  })
  const canClear = Boolean(clearable && onClear && selectedValue && !disabled)
  const label = selectedOption?.label ?? 'Επιλογή'

  function updatePopoverLayout() {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return

    const width = Math.max(rect.width, 180)
    const margin = 8
    const left = Math.min(
      Math.max(rect.left, margin),
      window.innerWidth - width - margin,
    )
    const availableBelow = window.innerHeight - rect.bottom - margin
    const maxHeight = Math.min(280, Math.max(120, availableBelow))
    const top = rect.bottom + margin

    setPopoverLayout({ top, left, width, maxHeight })
  }

  useEffect(() => {
    if (!isOpen) return

    function closeOnOutsidePointerDown(e: PointerEvent) {
      const target = e.target as Node
      if (
        !rootRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', closeOnOutsidePointerDown)
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointerDown)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    updatePopoverLayout()
    window.addEventListener('resize', updatePopoverLayout)
    window.addEventListener('scroll', updatePopoverLayout, true)
    return () => {
      window.removeEventListener('resize', updatePopoverLayout)
      window.removeEventListener('scroll', updatePopoverLayout, true)
    }
  }, [isOpen])

  function emitChange(nextValue: string) {
    const event = {
      target: { value: nextValue, name },
      currentTarget: { value: nextValue, name },
    } as unknown as ChangeEvent<HTMLSelectElement>
    onChange?.(event)
  }

  function selectOption(option: ParsedSelectOption) {
    if (option.disabled) return
    emitChange(option.value)
    setIsOpen(false)
  }

  function toggleOpen() {
    if (disabled) return
    if (!isOpen) updatePopoverLayout()
    setIsOpen((current) => !current)
  }

  return (
    <div
      ref={rootRef}
      className="relative"
      onKeyDown={(e) => {
        if (e.key === 'Escape') setIsOpen(false)
      }}
    >
      <button
        ref={buttonRef}
        id={id}
        name={name}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={props['aria-label']}
        title={props.title}
        autoFocus={props.autoFocus}
        disabled={disabled}
        onBlur={(e) => {
          ;(onBlur as ((event: unknown) => void) | undefined)?.(e)
        }}
        onClick={(e) => {
          ;(onClick as ((event: unknown) => void) | undefined)?.(e)
          if (!e.defaultPrevented) toggleOpen()
        }}
        onFocus={(e) => {
          ;(onFocus as ((event: unknown) => void) | undefined)?.(e)
        }}
        style={style}
        className={cx(
          'w-full appearance-none border border-zinc-200 bg-white text-zinc-900 shadow-sm outline-none transition hover:border-zinc-300 hover:bg-zinc-50 focus:border-violet-500 focus:bg-white focus:ring-2 focus:ring-violet-500/25 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-100 dark:hover:border-zinc-600 dark:hover:bg-zinc-900 dark:focus:border-violet-400 dark:focus:bg-zinc-900 dark:focus:ring-violet-400/20 dark:disabled:bg-zinc-800/70 dark:disabled:text-zinc-500',
          selectButtonSizeClass[controlSize],
          controlHeightClass[controlSize],
          'relative flex items-center text-left',
          canClear ? 'pr-16' : 'pr-10',
          className,
        )}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {canClear ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label={clearLabel}
            title={clearLabel}
            onClick={(e) => {
              e.stopPropagation()
              onClear?.()
              setIsOpen(false)
            }}
            className="absolute right-9 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-zinc-100 text-zinc-500 ring-1 ring-zinc-200 transition hover:bg-red-50 hover:text-red-600 hover:ring-red-200 dark:bg-zinc-800 dark:text-zinc-400 dark:ring-zinc-700 dark:hover:bg-red-950/50 dark:hover:text-red-300 dark:hover:ring-red-900/60"
          >
            <ClearIcon className="h-3.5 w-3.5" />
          </span>
        ) : null}
        <span className="pointer-events-none absolute right-1 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-zinc-700 dark:text-zinc-300">
          <ChevronDownIcon className="h-5 w-5" />
        </span>
      </button>

      {isOpen && typeof document !== 'undefined' ? createPortal(
        <div
          ref={popoverRef}
          role="listbox"
          style={{
            top: popoverLayout.top,
            left: popoverLayout.left,
            width: popoverLayout.width,
            maxHeight: popoverLayout.maxHeight,
          }}
          className="fixed z-[9999] overflow-y-auto rounded-xl border border-zinc-200 bg-white p-1.5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setIsOpen(false)
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === selectedValue}
              disabled={option.disabled}
              onClick={() => selectOption(option)}
              className={cx(
                'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50',
                option.value === selectedValue
                  ? 'bg-violet-50 text-violet-800 dark:bg-violet-950/50 dark:text-violet-200'
                  : 'text-zinc-700 hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-zinc-800',
              )}
            >
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {option.value === selectedValue ? (
                <span className="text-violet-600 dark:text-violet-300" aria-hidden>
                  ✓
                </span>
              ) : null}
            </button>
          ))}
        </div>
      , document.body) : null}
    </div>
  )
}

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  controlSize?: ControlSize
}

export function Textarea({
  controlSize = 'lg',
  className,
  ...props
}: TextareaProps) {
  return (
    <textarea
      className={controlClass({ size: controlSize, className })}
      {...props}
    />
  )
}
